import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Checkbox, Divider, Select } from "antd";
import Search from "antd/lib/input/Search";
import {
  criticalActivityHeaderComponent,
  criticalActivityIndicatorCellRenderer,
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import NoLinkIcon from "components/svg-icons/no-link-icon";
import { GridLoadingIndicator } from "components/widgets";
import {
  ECustomUserTypes,
  EntityType,
  GanttActivityTypes
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  TActivityHookInfo,
  TEditingEntity,
  TFeatureActivityWithInstance,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DateUtils } from "utils/dateutils";
import { matchCustomUserProjectRole } from "utils/utils";
import AIPoweredIcon from "components/svg-icons/ai-powered-icon";
import "./activity-listing.scss";
import {
  getLinkType,
  getTLinkSourceActivityObject
} from "components/feature-entity-linking-page/utils";
import AIAdvanceLinkingSettings from "pages/bulk-linking/linking-ai-advance-settings";
import BlueTickIcon from "components/svg-icons/blue-tick-icon";
import AiPoweredUI from "components/welcome-startup-setup/ai-powered";
import { useAISuggestedActivityIdsHook } from "pages/bulk-linking/hooks/ai-suggested-activities";
import { CloseCircleOutlined } from "@ant-design/icons";

function CellWithDateCheckbox(params: any) {
  const { data, isStartDate, onCheckBoxAction, value, isWBSLinkingEnabled } =
    params;
  const checkBoxVisibility = useMemo(() => {
    if (!isWBSLinkingEnabled && data?.type === 5) {
      return value;
    }

    if (data?.type === 5 && isWBSLinkingEnabled) return true;
    return data?.type !== 5;
  }, [data?.type, isWBSLinkingEnabled, value]);

  const date = isStartDate ? data?.start_date : data?.end_date;

  if (!date) return "";
  const dateFormated = DateUtils.format(date, "MM-DD-YYYY");

  return (
    <div className="flex w-full justify-end items-center">
      {checkBoxVisibility ? (
        <Checkbox
          checked={value}
          onChange={(event) => {
            onCheckBoxAction(event.target.checked, data);
          }}
          data-testid="activitylisting-checkbox-date"
        >
          <i>
            <TruncatedTextWithTooltip text={dateFormated} />
          </i>
        </Checkbox>
      ) : (
        <i className="mr-2">{dateFormated}</i>
      )}
    </div>
  );
}

const cellWithTaskId = (params: any) => {
  if (params.data.type !== 5 || params?.node?.allChildrenCount) {
    return (
      <TruncatedTextWithTooltip text={params.data.feature_instance.number} />
    );
  }

  if (params.data.type === 5 || !params?.node?.allChildrenCount) {
    return (
      <TruncatedTextWithTooltip
        text={params.data.feature_instance.number}
        disableTooltip
      />
    );
  }

  return (
    <div className="flex items-center">
      <div className="flex h-5 w-5 items-center">
        <NoLinkIcon fill="#3B3B3B33" size={18} />
      </div>
      <i className="mr-2">
        <TruncatedTextWithTooltip
          text={params.data.feature_instance.number}
          tooltipContent="You can only link an activity, milestones can't be linked."
        />
        ;
      </i>
    </div>
  );
};

type OptionType = {
  isLinkedWithStartdate: boolean;
  id: string;
  type: string;
};

export default function ActivityListing({
  activityListHook,
  editingEntity,
  onUpdateEditingEntityList,
  setEditingEntity
}: {
  activityListHook: TActivityHookInfo;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
}) {
  const {
    activities,
    isCSVFile,
    hasActive,
    scheduleVersionsOptions,
    selectedScheduleId,
    createActiveSchedule
  } = activityListHook;
  const { tokenContents, isFeatureFlagEnabled, tokenRetrievalState } =
    useContext(ProjectContext);
  const [isWBSLinkingEnabled, setWBSLinkingEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Array<OptionType>>([]);

  const gridRef = useRef<AgGridReact<any>>(null);
  const [isAISuggested, setAISuggested] = useState(false);
  const isSmartSetupEnabled = isFeatureFlagEnabled("SMART_LINKING");

  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const onClose = useCallback(() => {
    setEditingEntity((pre) => {
      if (!pre) return pre;
      return { ...pre, parentEntity: null };
    });
  }, [setEditingEntity]);

  const elementRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(
    (event: any) => {
      const domNode = elementRef?.current;
      if (!domNode || !domNode?.contains(event.target)) {
        const skipWindows = document.querySelectorAll(".skip-listing-window");
        const canSkip = Array.from(skipWindows).some(
          (v) => v?.contains && v.contains(event.target)
        );
        if (!canSkip) {
          setTimeout(() => {
            onClose();
          }, 100);
        }
      }
    },
    [onClose]
  );
  const componentDidMount = useCallback(() => {
    document.addEventListener("click", handleClickOutside, true);
  }, [handleClickOutside]);

  const componentWillUnmount = useCallback(() => {
    document.removeEventListener("click", handleClickOutside, true);
  }, [handleClickOutside]);

  useEffect(() => {
    componentDidMount();
    return componentWillUnmount;
  }, [componentDidMount, componentWillUnmount]);

  const { suggestedIds, loading } = useAISuggestedActivityIdsHook({
    featureId: editingEntity?.entityDetail?.id,
    token: tokenRetrievalState.token,
    entiyType: EntityType.Material,
    isEnabled: isSmartSetupEnabled
  });

  useEffect(() => {
    let list: TLinkSource[] = editingEntity?.list || [];

    if (editingEntity?.parentEntity) {
      const parentLink = editingEntity?.list.find(
        (x) =>
          x.feature_instance?.id ===
          editingEntity.parentEntity?.link.featureInstanceId
      );
      if (parentLink) {
        list = parentLink.feature_instance?.links || [];
      }
    }

    const filters = list
      .map((x: TLinkSource) => {
        const sourceTarget = x?.feature_instance;
        const featureActivities = sourceTarget?.feature_activities?.length
          ? sourceTarget?.feature_activities[0]
          : null;

        return sourceTarget?.id
          ? {
              link_type: x.link_type,
              id: sourceTarget.id,
              type: String(featureActivities?.type),
              isLinkedWithStartdate: x.isLinkedWithStartdate
            }
          : undefined;
      })
      .filter((x: OptionType | undefined) => x !== undefined) as OptionType[];

    setSelectedIds(filters);
  }, [editingEntity]);

  useEffect(() => {
    const showAISuggestions = async () => {
      const filter = await gridRef.current?.api?.getColumnFilterInstance(
        "ai_icon"
      );
      const values = isAISuggested
        ? ["showAiIcon"]
        : ["showAiIcon", "hideAiIcon"];
      filter?.setModel({ values });
      gridRef?.current?.api?.onFilterChanged();
    };
    if (isSmartSetupEnabled) {
      showAISuggestions();
    }
  }, [isAISuggested, isSmartSetupEnabled]);

  const isWBSLinkExist = useMemo(() => {
    return selectedIds?.some((item: any) => item?.type === 5);
  }, [selectedIds]);

  const rowData = useMemo(() => {
    const filteredTasks = activities?.filter(
      (ts: TFeatureActivityWithInstance) => ts.type !== 3 && ts.type !== 4
    );

    const allTasks: any[] = [];

    filteredTasks.forEach((tk) => {
      const task = { ...tk };
      task.is_suggested =
        suggestedIds && suggestedIds?.length
          ? suggestedIds?.some(
              (item: any) => item?.id === task?.feature_instance_id
            )
          : false;

      allTasks.push(task);
    });

    const addParent = (t: TFeatureActivityWithInstance, org: Array<any>) => {
      if (t.parent) {
        const parent = allTasks?.find(
          (ts: TFeatureActivityWithInstance) =>
            ts.feature_instance_id === t.parent
        );
        if (parent) addParent(parent, org);
      }
      org.push(t.feature_instance_id);
    };
    const tree = allTasks?.map((t: TFeatureActivityWithInstance) => {
      const org: any[] = [];
      if (t) addParent(t, org);
      return { org, ...t };
    });

    return tree;
  }, [activities, suggestedIds]);

  const updateForChildEntity = useCallback(
    (
      isCheckBoxFromStartDate: boolean,
      checked: boolean,
      item: TFeatureActivityWithInstance
    ) => {
      if (!editingEntity?.parentEntity || !editingEntity?.parentEntity?.link)
        return;
      const materialLink = editingEntity.list.find(
        (x) =>
          x.featureInstanceId ===
          editingEntity?.parentEntity?.link.featureInstanceId
      );

      const material = materialLink?.feature_instance;
      if (!material) return;
      const remainingList = editingEntity.list.filter(
        (x) => x.featureInstanceId !== material.id
      );

      if (!material || !material.links) return;

      let activityList: TLinkSource[] = [...material.links];

      if (checked) {
        const existingIndex = material.links.findIndex(
          (i) =>
            i.feature_instance?.id === item.feature_instance_id &&
            i.isLinkedWithStartdate === !isCheckBoxFromStartDate
        );
        if (existingIndex !== -1) {
          const activityItem = material.links[existingIndex];

          const updatedlist = [
            ...material.links.slice(0, existingIndex),
            {
              ...activityItem,
              isLinkedWithStartdate: isCheckBoxFromStartDate,
              link_type: getLinkType(
                activityItem.reverse_dependency,
                isCheckBoxFromStartDate
              )
            },
            ...material.links.slice(existingIndex + 1)
          ];
          activityList = updatedlist;
        } else {
          const newActivity = getTLinkSourceActivityObject(
            item,
            isCheckBoxFromStartDate
          );
          activityList.push(newActivity);
        }
      } else {
        activityList = activityList.filter(
          (x) => !(x.feature_instance?.id === item.feature_instance_id)
        );
      }

      const newEditingEntity: TEditingEntity = editingEntity
        ? {
            ...editingEntity,
            list: [
              ...remainingList,
              {
                ...materialLink,
                feature_instance: { ...material, links: activityList }
              }
            ]
          }
        : editingEntity;
      setEditingEntity(newEditingEntity);
    },
    [editingEntity, setEditingEntity]
  );

  const onChangeCheckbox = useCallback(
    (
      isCheckBoxFromStartDate: boolean,
      checked: boolean,
      item: TFeatureActivityWithInstance
    ) => {
      if (editingEntity.parentEntity) {
        updateForChildEntity(isCheckBoxFromStartDate, checked, item);
        return;
      }
      if (checked) {
        const existingIndex = editingEntity.list.findIndex(
          (i) =>
            i.feature_instance?.id === item.feature_instance_id &&
            i.isLinkedWithStartdate === !isCheckBoxFromStartDate
        );
        if (existingIndex !== -1) {
          const activityItem = editingEntity.list[existingIndex];

          const updatedlist = [
            ...editingEntity.list.slice(0, existingIndex),
            {
              ...activityItem,
              isLinkedWithStartdate: isCheckBoxFromStartDate,
              link_type: getLinkType(
                activityItem.reverse_dependency,
                isCheckBoxFromStartDate
              )
            },
            ...editingEntity.list.slice(existingIndex + 1)
          ];

          onUpdateEditingEntityList({
            updatedlist
          });
          return;
        }

        const newActivity = getTLinkSourceActivityObject(
          item,
          isCheckBoxFromStartDate
        );
        onUpdateEditingEntityList({
          updatedlist: [...editingEntity.list, newActivity]
        });
      } else {
        onUpdateEditingEntityList({
          updatedlist: editingEntity.list.filter(
            (x) => !(x.feature_instance?.id === item.feature_instance_id)
          )
        });
      }
    },
    [
      editingEntity.parentEntity,
      editingEntity.list,
      onUpdateEditingEntityList,
      updateForChildEntity
    ]
  );

  const aiIconCellRenderer = ({ data }: any) => {
    return data?.is_suggested ? (
      <div className="items-center w-1">
        <AIPoweredIcon />
      </div>
    ) : null;
  };

  const btnCss = useMemo(() => {
    return isAISuggested
      ? "text-ciq-blue-600 border-ciq-blue-600 focus:text-ciq-blue-600 focus:border-ciq-blue-600"
      : "text-black text-opacity-90 border-ciq-gray-100 focus:text-black text-opacity-90 focus:border-ciq-gray-100";
  }, [isAISuggested]);

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    const colArr: ColDef[] = [
      {
        colId: "ai_icon",
        field: "ai_icon",
        headerName: "",
        headerTooltip: "",
        cellRenderer: aiIconCellRenderer,
        minWidth: 20,
        maxWidth: 20,
        resizable: false,
        showRowGroup: false,
        filter: true,
        valueGetter: ({ data }: any) => {
          return data?.is_suggested ? "showAiIcon" : "hideAiIcon";
        }
      },
      {
        colId: "source_task_id",
        field: "feature_instance.number",
        headerName: "ID",
        headerTooltip: "ID",
        showRowGroup: true,
        filter: true,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: cellWithTaskId
        },
        sort: "desc",
        minWidth: 150
      },
      {
        colId: "text",
        field: "feature_instance.title",
        headerName: "Name",
        headerTooltip: "NAME",
        filter: true,
        flex: 1,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "critical",
        field: "critical",
        headerName: "",
        headerTooltip: "CRITICAL ACTIVITY",
        headerComponentParams: criticalActivityHeaderComponent,
        cellRenderer: criticalActivityIndicatorCellRenderer,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params?.value === true ? "Critical" : "Non Critical";
          },
          comparator: (valA: any, valB: any) => {
            if (valA === valB) return 0;
            return valA > valB ? -1 : 1;
          }
        },
        minWidth: 90,
        maxWidth: 90,
        getQuickFilterText: () => ""
      },
      {
        colId: "start_date",
        field: "start_date",
        headerName: "Start Date",
        headerTooltip: "START DATE",
        headerClass: "dateHedderCell",
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: true,
          onCheckBoxAction: (
            checked: boolean,
            data: TFeatureActivityWithInstance
          ) => onChangeCheckbox(true, checked, data),
          isWBSLinkingEnabled
        },
        valueGetter: ({ data }) =>
          selectedIds.some(
            (x) => x.id === data.feature_instance_id && x.isLinkedWithStartdate
          ),
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => "",
        pinned: "right"
      },
      {
        colId: "end_date",
        field: "end_date",
        headerName: "End Date",
        headerTooltip: "END DATE",
        headerClass: "dateHedderCell",
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: false,
          onCheckBoxAction: (
            checked: boolean,
            data: TFeatureActivityWithInstance
          ) => onChangeCheckbox(false, checked, data),
          isWBSLinkingEnabled
        },
        valueGetter: ({ data }) =>
          selectedIds.some(
            (x) => x.id === data.feature_instance_id && !x.isLinkedWithStartdate
          ),
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => "",
        pinned: "right"
      },
      {
        colId: "total_slack",
        field: "total_slack",
        headerName: "Activity Float",
        headerTooltip: "ACTIVITY FLOAT",
        cellStyle: (params: any) => {
          if (params?.data?.critical) {
            return { fontWeight: "bold" };
          }
          return { fontWeight: "normal" };
        },
        valueGetter: (params) => {
          if (
            params?.data?.total_slack === null ||
            params.data.type === GanttActivityTypes.Wps
          )
            return "";
          const float = Math.floor((params?.data?.total_slack || 0) / 8);
          return `${float} days`;
        },
        minWidth: 100,
        getQuickFilterText: () => "",
        cellRenderer: truncatedTextCellRenderer
      }
    ] as ColDef[];

    if (!isGCUser || isCSVFile) {
      return colArr
        ?.filter(
          (col: ColDef) =>
            col.colId !== "critical" && col.colId !== "total_slack"
        )
        .map((x) => {
          if (x.colId === "source_task_id") {
            return {
              ...x,
              minWidth: 200,
              width: 250
            };
          }
          if (x.colId === "text") {
            return {
              ...x,
              minWidth: 300,
              width: 400
            };
          }
          return x;
        });
    }
    return colArr;
  }, [isWBSLinkingEnabled, isGCUser, isCSVFile, onChangeCheckbox, selectedIds]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: false,
      resizable: true,
      editable: false,
      menuTabs: [],
      cellRendererParams: {
        suppressCount: true
      }
    };
  }, []);

  const getDataPath = useMemo(() => {
    return (data: any) => {
      return data.org;
    };
  }, []);

  const rowClass = "ai-row-suggested";

  const getRowClass = useCallback(({ data }) => {
    if (data?.is_suggested) {
      return rowClass;
    }
    return "ai-row-unsuggested";
  }, []);

  const suggestionDiv = isSmartSetupEnabled ? (
    <>
      <Divider type="vertical" className="h-7 m-0 mr-4" />
      <Button
        icon={isAISuggested ? <BlueTickIcon /> : null}
        className={`${btnCss} flex justify-center items-center  rounded p-1 gap-x-1 px-3`}
        onClick={() => setAISuggested(!isAISuggested)}
        loading={loading}
        disabled={loading}
      >
        <span>Filter Suggestions</span>
        <span>
          <AiPoweredUI label="AI" />
        </span>
      </Button>
    </>
  ) : (
    <Divider type="vertical" className="h-7 m-0 mr-1" />
  );

  return (
    <div className="flex flex-col h-full w-full activity-listing ai-row-style skip-listing-window">
      <div className="flex flex-col w-full px-6 py-2">
        <div className="flex items-center justify-between pb-1">
          <div className="flex font-bold space-x-1 pb-1">
            <div>List of Activities</div>

            {editingEntity.parentEntity && (
              <div className="flex pl-1 text-black text-opacity-60">
                (
                <div
                  className="italic px-1"
                  data-testid="activitylisting-parent-entity-title"
                >
                  {editingEntity.parentEntity.link.feature_instance?.title}
                </div>
                )
              </div>
            )}
          </div>
          {editingEntity.parentEntity && (
            <div
              className="flex justify-center items-center mr-1"
              data-testid="activitylisting-close-button"
            >
              <CloseCircleOutlined title="Close" onClick={onClose} />
            </div>
          )}
        </div>

        {!hasActive ? (
          <div className="flex items-center space-x-3">
            <Select
              options={scheduleVersionsOptions}
              className="w-64"
              defaultValue={selectedScheduleId}
              onChange={(id) => {
                if (id && typeof id === "string") createActiveSchedule(id);
              }}
              data-testid="activitylisting-select-scheduleid"
            />
            <Search
              placeholder="Search Activity Name or ID"
              className="w-full"
              onChange={(e) => {
                const text = (e.target.value || "").toLowerCase().trim();
                gridRef.current?.api?.setGridOption("quickFilterText", text);
              }}
              data-testid="activitylisting-search-activity-name-id"
            />

            {suggestionDiv}
            <AIAdvanceLinkingSettings
              isWBSLinkExist={isWBSLinkExist}
              onWBSEnableAction={(isChecked: boolean) =>
                setWBSLinkingEnabled(isChecked)
              }
            />
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <Search
              placeholder="Search Activity Name or ID"
              className="w-full"
              data-testid="activitylisting-search-activity-name-id"
              onChange={(e) => {
                const text = (e.target.value || "").toLowerCase().trim();
                gridRef.current?.api?.setGridOption("quickFilterText", text);
              }}
            />
            {suggestionDiv}

            <AIAdvanceLinkingSettings
              isWBSLinkExist={isWBSLinkExist}
              onWBSEnableAction={(isChecked: boolean) =>
                setWBSLinkingEnabled(isChecked)
              }
            />
          </div>
        )}
      </div>

      <AgGridReact
        className="ag-theme-alpine non-border ai-row-style"
        //  style={{ height: 600 }}
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows={false}
        headerHeight={40}
        treeData
        loadingOverlayComponent={GridLoadingIndicator}
        rowSelection={{
          mode: "singleRow",
          checkboxes: false
        }}
        groupDefaultExpanded={-1}
        getDataPath={getDataPath}
        groupDisplayType="custom"
        suppressCellFocus
        suppressDragLeaveHidesColumns
        suppressMovableColumns
        excludeChildrenWhenTreeDataFiltering
        getRowId={(param) => param.data?.feature_instance_id}
        rowClass={rowClass}
        getRowClass={getRowClass}
      />
    </div>
  );
}
